/**
 * Shared search + sort (+ optional org facet) for Obchody / Organizace pickers.
 * Mobile: always-visible search + Filters button → sheet (sort/org only).
 * sm+: inline search + sort + org.
 */

import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CUSTOMER_FORM_DIRTY_ATTR } from '../../app/pwa/customerFormDirtyGate.js';
import { DirectoryFilterButton } from './DirectoryFilterButton.js';
import {
  DirectoryFilterFacets,
  DirectoryFilterSheet,
} from './DirectoryFilterSheet.js';
import { DirectorySearchField } from './DirectorySearchField.js';
import type { DirectoryOrgFacet, DirectorySortId } from './directoryListFilter.js';

const SEARCH_DEBOUNCE_MS = 200;
/** Tailwind `sm` breakpoint — inline filters at this width and above. */
const SM_UP_MQ = '(min-width: 640px)';

export interface DirectoryListFiltersProps {
  readonly query: string;
  readonly onQueryChange: (query: string) => void;
  readonly sort: DirectorySortId;
  readonly onSortChange: (sort: DirectorySortId) => void;
  /** When set, show organization facet (platform Obchody). */
  readonly orgFacets?: readonly DirectoryOrgFacet[];
  readonly orgCode?: string | null;
  readonly onOrgCodeChange?: (orgCode: string | null) => void;
  /** Extra sort options beyond nameAsc/nameDesc (e.g. orgAsc for shops). */
  readonly sortOptions?: readonly DirectorySortId[];
  /**
   * Sort id treated as inactive / clear-all target (shops: nameAsc).
   * Orders pass `dateDesc` so Filters chrome matches the list default.
   */
  readonly defaultSort?: DirectorySortId;
  readonly searchPlaceholder: string;
  /** Override default 55%-width search field (e.g. Objednávky capped rem max). */
  readonly searchFieldClassName?: string;
  readonly testIdPrefix?: string;
}

const DEFAULT_SORT_OPTIONS: readonly DirectorySortId[] = ['nameAsc', 'nameDesc'];

function useSmUp(): boolean {
  const [smUp, setSmUp] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return true;
    }
    return window.matchMedia(SM_UP_MQ).matches;
  });

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') {
      return;
    }
    const media = window.matchMedia(SM_UP_MQ);
    const onChange = (): void => {
      setSmUp(media.matches);
    };
    onChange();
    media.addEventListener('change', onChange);
    return () => {
      media.removeEventListener('change', onChange);
    };
  }, []);

  return smUp;
}

export function DirectoryListFilters(props: DirectoryListFiltersProps): JSX.Element {
  const {
    query,
    onQueryChange,
    sort,
    onSortChange,
    orgFacets,
    orgCode = null,
    onOrgCodeChange,
    sortOptions = DEFAULT_SORT_OPTIONS,
    defaultSort = 'nameAsc',
    searchPlaceholder,
    searchFieldClassName,
    testIdPrefix = 'directory-filters',
  } = props;
  const { t } = useTranslation('customer');
  const sheetTitleId = useId();
  const smUp = useSmUp();
  const [draft, setDraft] = useState(query);
  const [syncedQuery, setSyncedQuery] = useState(query);
  const [sheetOpen, setSheetOpen] = useState(false);

  // Parent-driven resets (e.g. Clear filters) — adjust draft during render (React docs).
  if (query !== syncedQuery) {
    setSyncedQuery(query);
    setDraft(query);
  }

  useEffect(() => {
    const handle = window.setTimeout(() => {
      if (draft !== query) {
        onQueryChange(draft);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [draft, onQueryChange, query]);

  // Close sheet when crossing to desktop so focus/body-lock cannot leak.
  if (smUp && sheetOpen) {
    setSheetOpen(false);
  }

  const showOrgFacet =
    orgFacets !== undefined && orgFacets.length > 1 && onOrgCodeChange !== undefined;

  /** Facet-only active state — query alone does not mark Filters active. */
  const facetsActive =
    sort !== defaultSort || (orgCode !== null && orgCode.length > 0);

  /** Uncommitted search draft (debounce) — blocks pull-to-refresh wipe. */
  const isDraftDirty = draft !== query;

  const searchField = (
    <DirectorySearchField
      draft={draft}
      onDraftChange={setDraft}
      onCommit={(value) => {
        onQueryChange(value);
      }}
      onClear={() => {
        setDraft('');
        onQueryChange('');
      }}
      placeholder={searchPlaceholder}
      ariaLabel={t('directory.filters.searchAria')}
      clearAriaLabel={t('directory.filters.clear')}
      testIdPrefix={testIdPrefix}
      className={
        searchFieldClassName ??
        (smUp
          ? 'w-full max-w-[55%]'
          : 'min-w-0 max-w-[55%] flex-[3] basis-[55%]')
      }
    />
  );

  const facets = (
    <DirectoryFilterFacets
      sort={sort}
      onSortChange={onSortChange}
      showOrgFacet={showOrgFacet}
      orgFacets={orgFacets}
      orgCode={orgCode}
      onOrgCodeChange={onOrgCodeChange}
      sortOptions={sortOptions}
      testIdPrefix={testIdPrefix}
      t={t}
    />
  );

  if (smUp) {
    return (
      <div
        data-testid={testIdPrefix}
        role="search"
        // items-end: align search input with sort/org selects (not column centers —
        // facets have a label above the control; search does not).
        className="flex flex-col gap-2.5 lg:flex-row lg:items-end lg:gap-4"
        {...{ [CUSTOMER_FORM_DIRTY_ATTR]: isDraftDirty ? 'true' : 'false' }}
      >
        {searchField}
        {facets}
      </div>
    );
  }

  return (
    <div
      data-testid={testIdPrefix}
      role="search"
      className="flex flex-col gap-2"
      {...{ [CUSTOMER_FORM_DIRTY_ATTR]: isDraftDirty ? 'true' : 'false' }}
    >
      <div className="flex items-center gap-2">
        {searchField}
        <DirectoryFilterButton
          active={facetsActive}
          expanded={sheetOpen}
          onClick={() => {
            setSheetOpen(true);
          }}
          label={
            facetsActive
              ? t('directory.filters.openFiltersActive')
              : t('directory.filters.openFilters')
          }
          testIdPrefix={testIdPrefix}
          className="shrink-0 whitespace-nowrap"
        />
      </div>
      {facetsActive ? (
        <p
          className="text-xs text-[var(--color-on-surface-muted)]"
          data-testid={`${testIdPrefix}-active-summary`}
        >
          {t(`directory.filters.sort.${sort}`)}
          {orgCode !== null && orgCode.length > 0
            ? ` · ${t('directory.filters.orgLabel')}: ${orgCode}`
            : null}
        </p>
      ) : null}
      <DirectoryFilterSheet
        open={sheetOpen}
        onClose={() => {
          setSheetOpen(false);
        }}
        title={t('directory.filters.sheetTitle')}
        titleId={sheetTitleId}
        closeLabel={t('shared.cancel')}
        testIdPrefix={testIdPrefix}
        facetsActive={facetsActive}
        onClearAll={() => {
          onSortChange(defaultSort);
          onOrgCodeChange?.(null);
        }}
        clearAllLabel={t('directory.filters.clearAll')}
        doneLabel={t('directory.filters.done')}
      >
        {facets}
      </DirectoryFilterSheet>
    </div>
  );
}
